import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import CustomTable from '../../../../../components/table/CustomTable';
import StatusChip from '../../../../../components/statusChip/StatusChip';
import { PlusIcon } from '../../../../../assets/icons';
import { routes } from '../../../../../routes/pageRoutes';
import Axios, { authHeaders } from '../../../../../utils/axios';
import CreateCompanyModal from '../../../../../components/modals/UserProfile/CreateCompanyModal';
import EditCompanyProfileModal from '../../../../../components/modals/company/EditCompanyProfileModal';
import DeletePopup from '../../../../../components/delete-popup';
import { addUserCompany } from '../../../../../redux/slice/allUsersSlice';
import { updateCompanyDetails, deleteCompany } from '../../../../../redux/slice/companySlice';
import PreviewModal from '../../../../../components/modals/previewModal';
import { useToast } from '../../../../../context/TostContext';
import { IconBtn } from '../../../../../components/commonButton/btns';
import { UserInital } from '../../../../../utils/otherHelopers';

const Companies = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToast();
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [companiesList, setCompaniesList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewMediaArray, setPreviewMediaArray] = useState([]);

    const fetchCompanies = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await Axios.get(`/companies`, {
                params: {
                    user_id: id,
                    page,
                    limit
                },
                ...authHeaders()
            });
            const result = response.data?.data;
            setCompaniesList(result?.data || result || []);
            setTotal(response.data?.meta?.total || response.data?.data?.meta?.total || 0);
        } catch (error) {
            console.error("Failed to fetch companies:", error);
        } finally {
            setLoading(false);
        }
    }, [id, page, limit]);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    const handleCreateCompany = (formData) => {
        setSubmitting(true);
        dispatch(addUserCompany({ id, data: formData }))
            .unwrap()
            .then(() => {
                toast.success("Company created successfully");
                setShowCreateModal(false);
                fetchCompanies();
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to create company");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleEditCompany = (formData) => {
        if (!selectedCompany?.id) return;
        setSubmitting(true);
        dispatch(updateCompanyDetails({ id: selectedCompany.id, data: formData }))
            .unwrap()
            .then(() => {
                toast.success("Company updated successfully");
                setShowEditModal(false);
                setSelectedCompany(null);
                fetchCompanies();
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to update company");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleDeleteCompany = () => {
        if (!itemToDelete) return;
        setSubmitting(true);
        dispatch(deleteCompany(itemToDelete))
            .unwrap()
            .then(() => {
                toast.success("Company deleted successfully");
                setShowDeletePopup(false);
                setItemToDelete(null);
                fetchCompanies();
            })
            .catch((error) => {
                toast.error(error?.message || "Failed to delete company");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const actionList = [
        { title: 'View', handler: (row) => { navigate(routes.viewCompany.replace(":id", row.id)); } },
        { title: 'Edit', handler: (row) => { setSelectedCompany(row); setShowEditModal(true); } },
        { title: 'Delete', handler: (row) => { setItemToDelete(row.id); setShowDeletePopup(true); }, isDanger: true },
    ];
    const columns = [

        {
            header: 'Logo',
            cell: (_, item) => (
                <UserInital image={item.profile?.logo?.url} name={item.name} onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setPreviewMediaArray([{
                        url: item.profile?.logo?.url || "/images/AllUser/all-companies.png",
                        title: item.name || "Company Logo",
                        type: "image"
                    }]);
                    setShowPreviewModal(true);
                }}
                    onError={(e) => { e.target.src = "/images/AllUser/all-companies.png"; }} />
            )
        },
        {
            header: 'Company Name',
            cell: (_, item) => <span className="fw-medium">{item.name || "-"}</span>
        },
        {
            header: 'Category',
            cell: (_, item) => item.category?.name || "-"
        },
        {
            header: 'Mobile',
            cell: (_, item) => item.profile?.mobile_number || "-"
        },
        {
            header: 'Email',
            cell: (_, item) => item.profile?.email || "-"
        },
        {
            header: 'Followers',
            cell: (_, item) => item.followers_count ?? 0
        },
        {
            header: 'Verification Status',
            cell: (_, item) => (
                <StatusChip
                    pill
                    bg={
                        !item.verification ? "warning" :
                            item.verification.overall_status === "approved" ? "success" :
                                item.verification.overall_status === "rejected" ? "danger" :
                                    item.verification.overall_status === "submitted" ? "blue" :
                                        "blue"
                    }
                    title={
                        !item.verification ? "PENDING" :
                            item.verification.overall_status === "approved" ? "APPROVED" :
                                item.verification.overall_status === "rejected" ? "REJECTED" :
                                    item.verification.overall_status === "submitted" ? "SUBMITTED" :
                                        "IN PROGRESS"
                    }
                />
            )
        },
        {
            header: 'Status',
            cell: (_, item) => (
                <StatusChip
                    pill
                    bg={item.status === "active" ? "success" : "danger"}
                    title={item.status?.toUpperCase() || "INACTIVE"}
                />
            )
        },
        {
            header: 'Created At',
            cell: (_, item) => formatMomentDate(item.created_at, "DD-MM-YYYY hh:mm A")
        },
        {
            header: 'Updated At',
            cell: (_, item) => formatMomentDate(item.updated_at, "DD-MM-YYYY hh:mm A")
        }
    ];

    const totalPages = Math.ceil(total / limit);

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Companies</h6>
                <IconBtn size={32} icon={<PlusIcon size={16} />} className='rounded-circle' onClick={() => {
                    setShowCreateModal(true);
                }} />
            </div>

            <div className="border rounded-3 overflow-hidden">
                <CustomTable
                    columns={columns}
                    data={companiesList}
                    loading={loading}
                    actionList={actionList}
                    emptyMessage="No companies found."
                    pagination={{
                        currentPage: page,
                        totalPages,
                        total,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </div>

            <CreateCompanyModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateCompany}
                isLoading={submitting}
                title="Create Company"
                size="lg"
            />

            <EditCompanyProfileModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedCompany(null);
                }}
                onSubmit={handleEditCompany}
                company={selectedCompany}
                isLoading={submitting}
                title="Edit Company Profile"
                size="lg"
            />

            <DeletePopup
                show={showDeletePopup}
                onHide={() => {
                    setShowDeletePopup(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleDeleteCompany}
                isLoading={submitting}
                saveTitle="Delete"
            />

            <PreviewModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                previewMedia={previewMediaArray}
                initialIndex={0}
            />
        </div>
    );
};

export default Companies;
